// @intent: Zod schemas for RFC 6902 Patch shape + runtime Project/Scene/Layer shapes
// @flow: patchGate stage 1 calls PatchSchema.safeParse; handleTurn validates project state via ProjectSchema
// @stale: when patch op set grows (W5 = replace/add/remove; future = move/copy) or Layer types expand

import { z } from 'zod';

const JsonPointerSchema = z.string().regex(
  /^(\/(?:[^/~]|~0|~1|-)*)*$/,
  'invalid JSON Pointer; must start with / or be empty',
);

export const PatchOpSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('replace'),
    path: JsonPointerSchema,
    value: z.unknown(),
  }),
  z.object({
    op: z.literal('add'),
    path: JsonPointerSchema,
    value: z.unknown(),
  }),
  z.object({
    op: z.literal('remove'),
    path: JsonPointerSchema,
  }),
  z.object({
    op: z.literal('move'),
    from: JsonPointerSchema,
    path: JsonPointerSchema,
  }),
  z.object({
    op: z.literal('copy'),
    from: JsonPointerSchema,
    path: JsonPointerSchema,
  }),
]);

export const PatchSchema = z.array(PatchOpSchema);

// ─── R1.5 Runtime Project/SceneGraph/Layer schemas ────────────────────────────

/** Base fields shared by every layer type */
const BaseLayerShape = {
  id: z.string(),
  name: z.string(),
  visible: z.boolean(),
  locked: z.boolean(),
  x: z.number(),
  y: z.number(),
};

type SceneNode =
  | {
      id: string;
      name: string;
      type: 'scene';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      children: SceneNode[];
    }
  | {
      id: string;
      name: string;
      type: 'group';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      expanded?: boolean | undefined;
      children: SceneNode[];
    }
  | {
      id: string;
      name: string;
      type: 'rect';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      width: number;
      height: number;
      color: string;
      assetUrl?: string | undefined;
      alpha?: number | undefined;
    }
  | {
      id: string;
      name: string;
      type: 'sprite';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      width: number;
      height: number;
      assetUrl: string;
      tint?: string | undefined;
      alpha?: number | undefined;
    }
  | {
      id: string;
      name: string;
      type: 'text';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      text: string;
      fontSize: number;
      color: string;
      fontFamily?: string | undefined;
    }
  | {
      id: string;
      name: string;
      type: 'button';
      visible: boolean;
      locked: boolean;
      x: number;
      y: number;
      width: number;
      height: number;
      label: string;
      color: string;
      assetUrl?: string | undefined;
    };

const RectLayerSchema = z.object({
  ...BaseLayerShape,
  type: z.literal('rect'),
  width: z.number(),
  height: z.number(),
  color: z.string(),
  assetUrl: z.string().optional(),
  alpha: z.number().optional(),
}).strict();
const SpriteLayerSchema = z.object({
  ...BaseLayerShape,
  type: z.literal('sprite'),
  width: z.number(),
  height: z.number(),
  assetUrl: z.string(),
  tint: z.string().optional(),
  alpha: z.number().optional(),
}).strict();
const TextLayerSchema = z.object({
  ...BaseLayerShape,
  type: z.literal('text'),
  text: z.string(),
  fontSize: z.number(),
  color: z.string(),
  fontFamily: z.string().optional(),
}).strict();
const ButtonLayerSchema = z.object({
  ...BaseLayerShape,
  type: z.literal('button'),
  width: z.number(),
  height: z.number(),
  label: z.string(),
  color: z.string(),
  assetUrl: z.string().optional(),
}).strict();

/** Discriminated union of all layer types */
export const LayerSchema: z.ZodType<SceneNode> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({ ...BaseLayerShape, type: z.literal('scene'), children: z.array(LayerSchema).default([]) }).strict(),
    z.object({ ...BaseLayerShape, type: z.literal('group'), expanded: z.boolean().optional(), children: z.array(LayerSchema).default([]) }).strict(),
    RectLayerSchema,
    SpriteLayerSchema,
    TextLayerSchema,
    ButtonLayerSchema,
  ]),
);

export const SceneGraphSchema = z.object({
  version: z.literal('v1'),
  root: LayerSchema.refine((node) => node.type === 'scene', 'Scene graph root must be a scene node'),
}).strict();

export const SceneSchema = SceneGraphSchema;

export const SlotMapSchema = z.object({
  version: z.literal('v1'),
  slots: z.record(z.string(), z.object({
    layerId: z.string(),
    label: z.string(),
    kind: z.string(),
    assetUrl: z.string().optional(),
    text: z.string().optional(),
    color: z.string().optional(),
    url: z.string().optional(),
  }).strict()),
}).strict();

export const ProjectSchema = z.object({
  id: z.string(),
  version: z.literal('v1'),
  manifest: z.object({
    name: z.string(),
    viewport: z.object({
      width: z.number(),
      height: z.number(),
    }).strict(),
    template: z.string().optional(),
  }).strict(),
  scene_graph: SceneGraphSchema,
  slot_map: SlotMapSchema,
  code: z.string().nullable(),
  assets: z.array(z.object({
    id: z.string(),
    url: z.string(),
    mimeType: z.string(),
  }).strict()),
  variables: z.record(z.string(), z.unknown()),
}).strict();
